import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ACTIVO, CARGANDO, FILTRADO, INACTIVO } from '../../constantes';
import api from '@/providers/api';
import RespuestaApi from '@/models/respuestas/resApi.model';
import { COMBO_COLORES_ESTADOS } from '@/helpers/urls';
import { mostrarAlertaError } from '@/helpers/alert';
import { OperacionListadoEjecutivo } from '@/models/respuestas/detalleOperacion.model';
import { sonObjetosIguales } from '@/helpers/objects';
import { filtrarOperaciones as funcionFiltro } from '@/redux/slices/operacionesSlice/filtrosOperacionesEjecutivo';
import { RangoSerializable } from '@/models/componentes/datePicker.model';

export type FiltrosOperaciones = {
  folio: string;
  estado: RangoSerializable;
  cliente: string;
};

export type ComboColoresEstados = {
  idEstado: number;
  nombre: string;
  colorPrimario: string;
  colorSecundario: string;
};

export type CargaUtilFiltroEjecutivo = {
  filtro: string;
  carga: string | Record<number, boolean>;
};

export const obtenerComboEstados = createAsyncThunk(
  'operaciones/estados',
  async () => {
    try {
      const {
        data: { resultado: comboEstados },
      } = await api.get<RespuestaApi<ComboColoresEstados[]>>(
        COMBO_COLORES_ESTADOS
      );

      const estados = comboEstados.reduce((acc, curr) => {
        acc[curr.idEstado] = curr;
        return acc;
      }, {} as Record<number, ComboColoresEstados>);

      return estados;
    } catch (err) {
      mostrarAlertaError('Hubo un error al obtener los estados');
    }
  }
);

export type FiltrosOpEjecutivo = {
  datosCliente: string;
  nroOperacion: string;
  estados: Record<number, boolean>;
};

const filtrosIniciales: FiltrosOpEjecutivo = {
  datosCliente: '',
  nroOperacion: '',
  estados: {},
};

const operacionesSlice = createSlice({
  name: 'operaciones',
  initialState: {
    estadosCombo: null as Record<number, ComboColoresEstados>,
    operaciones: [] as OperacionListadoEjecutivo[],
    filtradas: [] as OperacionListadoEjecutivo[],
    filtrosIniciales,
    filtrosActuales: filtrosIniciales,
    estado: INACTIVO,
  },
  reducers: {
    cargarOperaciones: (
      state,
      { payload }: PayloadAction<OperacionListadoEjecutivo[]>
    ) => {
      const estadosDisponibles = [
        ...new Set(payload.map(({ idEstado }) => idEstado)),
      ].reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {} as Record<number, boolean>);

      return {
        ...state,
        operaciones: payload,
        filtradas: payload,
        estado: ACTIVO,
        filtrosActuales: {
          ...state.filtrosActuales,
          estados: estadosDisponibles,
        },
        filtrosIniciales: {
          ...state.filtrosIniciales,
          estados: estadosDisponibles,
        },
      };
    },
    filtrarOperaciones: (
      state,
      { payload }: PayloadAction<CargaUtilFiltroEjecutivo>
    ) => {
      const { filtrosIniciales, filtrosActuales, operaciones } = state;
      const { carga, filtro } = payload;

      const todasLasOperaciones = JSON.parse(JSON.stringify(operaciones));

      const nuevosfiltros: FiltrosOpEjecutivo = JSON.parse(
        JSON.stringify({ ...filtrosActuales, [filtro]: carga })
      );

      const sonFiltrosPredeterminados =
        nuevosfiltros.datosCliente === filtrosIniciales.datosCliente &&
        nuevosfiltros.nroOperacion === filtrosIniciales.nroOperacion &&
        sonObjetosIguales(nuevosfiltros.estados, filtrosIniciales.estados);

      let nuevoEstado;
      let nuevasOperaciones;

      if (sonFiltrosPredeterminados) {
        nuevoEstado = ACTIVO;
        nuevasOperaciones = todasLasOperaciones;
      } else {
        nuevoEstado = FILTRADO;
        nuevasOperaciones = funcionFiltro(todasLasOperaciones, nuevosfiltros);
      }

      return {
        ...state,
        filtradas: nuevasOperaciones,
        estado: nuevoEstado,
        filtrosActuales: nuevosfiltros,
      };
    },
    cargarEstadosFiltro: (
      state,
      { payload }: PayloadAction<Record<number, boolean>>
    ) => {
      return {
        ...state,
        filtrosActuales: { ...state.filtrosActuales, estados: payload },
      };
    },

    limpiarFiltros: (state) => {
      return {
        ...state,
        estado: ACTIVO,
        filtradas: state.operaciones,
        filtrosActuales: { ...state.filtrosIniciales },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(obtenerComboEstados.pending, (state) => {
        state.estado = CARGANDO;
      })
      .addCase(obtenerComboEstados.fulfilled, (state, { payload }) => {
        state.estadosCombo = payload;
        state.estado = ACTIVO;
      });
  },
});

export const {
  cargarOperaciones,
  filtrarOperaciones,
  cargarEstadosFiltro,
  limpiarFiltros,
} = operacionesSlice.actions;

export default operacionesSlice.reducer;
